import { CreatePensionPlanTransferDto } from './interfaces/PensionPlanTransfer';

/** Cada parte del plan de origen: lo que se eligió, con su id y su nombre. */
interface TransferOriginPart {
	id: string;
	name: string;
}

interface BuildCreateTransferPayloadArgs {
	/** Id del plan de origen en el catálogo, tal cual sale del combobox. */
	catalogId: string;
	/** Código DGSFP del plan de origen. */
	dgsfpCode: string;
	/** Referencia que el modal genera para hablar con soporte. */
	requestId: string;
	userAgent: string;
	/**
	 * Plan de origen tal como lo eligió el cliente. Cada pieza viaja con su id y
	 * su nombre porque el `originPayload` es lo que lee operaciones para saber
	 * qué le dijo el cliente, no solo el identificador.
	 */
	origin: { entity: TransferOriginPart; fund: TransferOriginPart; plan: TransferOriginPart };
	/** Momento de la solicitud, en ISO. Se pasa para poder fijarlo en los tests. */
	now: string;
}

/**
 * Arma el cuerpo con el que el modal de la plataforma pide un traspaso.
 *
 * Vive fuera del componente porque lo que aquí se declara tiene consecuencias
 * lejos del formulario: el `kind` decide cómo lee la petición el KYC y si en
 * HubSpot la operación se lee como traspaso o como alta.
 *
 * El modal se monta en cuatro sitios (MiBeneficio, Integraciones, el dashboard
 * individual y Configurar beneficio) y los cuatro son lo mismo: un cliente que
 * ya está dentro pidiendo traer dinero de otra gestora. Por eso el `kind` puede
 * ir fijo. Si algún día este modal se monta en el onboarding —donde la misma
 * pantalla significaría además abrir plan—, el `kind` tiene que pasar a ser un
 * argumento: dejarlo fijo mentiría sobre lo que pide el cliente.
 */
export function buildCreateTransferPayload({
	catalogId,
	dgsfpCode,
	requestId,
	userAgent,
	origin,
	now
}: BuildCreateTransferPayloadArgs): CreatePensionPlanTransferDto {
	return {
		catalogId: catalogId.trim(),
		DGSFP_code: dgsfpCode.trim(),
		transferDate: now,
		status: 'requested',
		// Lo declara quien lo pide: el backend NO lo deduce del estado del usuario.
		// Este modal solo sirve para traer dinero de otra gestora a un plan que el
		// cliente ya tiene, así que siempre es un traspaso. Sin esto la petición
		// llega con `kind` nulo y el KYC la confunde con el alta del usuario.
		kind: 'EXTERNAL_TRANSFER',
		originPayload: {
			entity: origin.entity,
			fund: origin.fund,
			plan: origin.plan
		},
		metadata: {
			requestId,
			userAgent,
			createdAt: now
		}
	};
}
